<template>
  <div class="clinic-dashboard container py-5">
    <!-- Welcome kartı -->
    <div class="welcome-card">
      <div class="welcome-card__avatar">{{ initials }}</div>
      <div class="welcome-card__main">
        <h1 class="welcome-card__title" v-tx="'ClinicDashboardWelcome'">{{ greeting }}, {{ displayName }}</h1>
        <p class="welcome-card__sub">{{ subtitle }}</p>
      </div>
      <div class="welcome-card__date">{{ today }}</div>
    </div>

    <!-- Statistika -->
    <div class="stats-card">
      <div class="stat" v-for="s in stats" :key="s.key">
        <div class="stat-top">
          <span class="dot" :style="{ background: s.dot }"></span>
          <span class="num">{{ s.count }}</span>
        </div>
        <div class="label" v-tx="s.txKey">{{ s.label }}</div>
      </div>
    </div>

    <!-- Naviqasiya kartları -->
    <p class="nav-heading" v-tx="'ClinicDashboardNavHeading'">{{ navHeading }}</p>
    <div class="nav-grid">
      <div
        v-for="c in navCards"
        :key="c.key"
        class="nav-card"
        :style="{ '--accent': c.accent }"
        @click="goCard(c)"
      >
        <div class="nav-card__icon" :style="{ background: c.accent + '1A' }">
          <svg viewBox="0 0 24 24" fill="none" stroke="#475569" stroke-width="1.6">
            <path stroke-linecap="round" stroke-linejoin="round" :d="icons[c.icon]" />
          </svg>
          <span v-if="c.badge" class="nav-card__badge">{{ c.badge }}</span>
        </div>
        <h3 class="nav-card__title" v-tx="c.titleKey">{{ c.title }}</h3>
        <p class="nav-card__desc" v-tx="c.descKey">{{ c.desc }}</p>
        <span class="nav-card__link" :style="{ color: c.accent }" v-tx="c.linkKey">
          {{ c.link }} <span class="arrow">→</span>
        </span>
      </div>
    </div>

    <!-- Son fəaliyyətlər -->
    <div class="activity-head">
      <span class="activity-head__title" v-tx="'ClinicDashboardActivityHeading'">{{ activityHeading }}</span>
      <router-link class="activity-head__link" :to="{ name: 'my-applications' }" v-tx="'ClinicDashboardActivityShowAll'">
        {{ activityShowAll }} <span class="arrow">→</span>
      </router-link>
    </div>
    <div class="activity-list" v-if="activities.length">
      <div class="activity" v-for="(a, i) in activities" :key="i">
        <span class="activity__avatar" :style="{ background: avatarFor(a.nurseProfileId) }">{{ nameInitials(a.nurseFullName) }}</span>
        <div class="activity__main">
          <div class="activity__name">{{ a.nurseFullName }}</div>
          <div class="activity__desc">{{ activityText(a) }}</div>
        </div>
        <span class="activity__time">{{ relativeTime(a.date) }}</span>
      </div>
    </div>
    <div class="activity-empty" v-else>—</div>
  </div>
</template>

<script>
import axios from "axios";

const LOCALES = { az: "az-AZ", de: "de-DE", en: "en-US", ru: "ru-RU", tr: "tr-TR" };

const ICONS = {
  user: "M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z",
  clipboard: "M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z",
  mail: "M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75",
  cog: "M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
};

const ACTIVITY_PALETTE = ["#132B59", "#894FE5", "#0FA5C6", "#10B881", "#F39C12", "#EF4444"];

// status kodu → tərcümə (5 dil)
const STATUS_T = {
  Neu: { az: "Yeni", de: "Neu", en: "New", ru: "Новый", tr: "Yeni" },
  InPruefung: { az: "Yoxlanışda", de: "In Prüfung", en: "In review", ru: "На проверке", tr: "İncelemede" },
  Vorstellungsgespraech: { az: "Müsahibə", de: "Vorstellungsgespräch", en: "Interview", ru: "Собеседование", tr: "Mülakat" },
  Dokumentation: { az: "Sənədləşmə", de: "Dokumentation", en: "Documentation", ru: "Документы", tr: "Belgeleme" },
  Visaprozess: { az: "Viza prosesi", de: "Visaprozess", en: "Visa process", ru: "Виза", tr: "Vize süreci" },
  Eingestellt: { az: "İşə götürülüb", de: "Eingestellt", en: "Hired", ru: "Принят", tr: "İşe alındı" },
};

const T = {
  az: {
    welcome: "Xoş gəldiniz", navHeading: "Hara keçmək istəyirsiniz?", activityHeading: "Son fəaliyyətlər", activityShowAll: "Hamısına bax",
    statBewerber: "Namizədlər", statBewerbungen: "Müraciətlər", statEingestellt: "İşə götürülən",
    navKandidatenTitle: "Namizədlər", navKandidatenDesc: "Tibb bacılarını axtarın və profilləri görün.", navKandidatenLink: "{n} namizəd mövcuddur",
    navBewerbungenTitle: "Müraciətlər", navBewerbungenDesc: "Aktiv müraciətləri idarə edin və izləyin.", navBewerbungenLink: "{n} aktiv müraciət",
    navNachrichtenTitle: "Mesajlar", navNachrichtenDesc: "IGAplus-a sorğu göndərin və ticketləri idarə edin.", navNachrichtenLink: "{n} oxunmamış mesaj",
    navProfilTitle: "Profil", navProfilDesc: "Klinika məlumatları və parametrləri idarə edin.", navProfilLink: "Parametrləri idarə et",
    actHired: "Müqavilə imzalandı", actNew: "Yeni müraciət", actMovedTo: "„{x}\" mərhələsinə keçirildi", actDoc: "{doc} yükləndi",
    rtNow: "indi", rtMin: "{n} dəq əvvəl", rtHour: "{n} saat əvvəl", rtYesterday: "Dünən", rtDays: "{n} gün əvvəl",
  },
  de: {
    welcome: "Willkommen zurück", navHeading: "Wohin möchten Sie gehen?", activityHeading: "Letzte Aktivitäten", activityShowAll: "Alle anzeigen",
    statBewerber: "Bewerber", statBewerbungen: "Bewerbungen", statEingestellt: "Eingestellt",
    navKandidatenTitle: "Kandidaten", navKandidatenDesc: "Pflegefachkräfte suchen und Profile anzeigen.", navKandidatenLink: "{n} Bewerber verfügbar",
    navBewerbungenTitle: "Bewerbungen", navBewerbungenDesc: "Aktive Bewerbungen verwalten und Workflow verfolgen.", navBewerbungenLink: "{n} aktive Bewerbungen",
    navNachrichtenTitle: "Nachrichten", navNachrichtenDesc: "Anfragen an IGAplus senden und Tickets verwalten.", navNachrichtenLink: "{n} ungelesene Nachrichten",
    navProfilTitle: "Profil", navProfilDesc: "Klinikangaben und Einstellungen verwalten.", navProfilLink: "Einstellungen verwalten",
    actHired: "Vertrag unterzeichnet", actNew: "Neue Bewerbung", actMovedTo: "Zu „{x}\" verschoben", actDoc: "{doc} hochgeladen",
    rtNow: "Gerade eben", rtMin: "Vor {n}m", rtHour: "Vor {n}h", rtYesterday: "Gestern", rtDays: "Vor {n} Tagen",
  },
  en: {
    welcome: "Welcome back", navHeading: "Where would you like to go?", activityHeading: "Recent activities", activityShowAll: "View all",
    statBewerber: "Candidates", statBewerbungen: "Applications", statEingestellt: "Hired",
    navKandidatenTitle: "Candidates", navKandidatenDesc: "Search nurses and view profiles.", navKandidatenLink: "{n} candidates available",
    navBewerbungenTitle: "Applications", navBewerbungenDesc: "Manage active applications and track the workflow.", navBewerbungenLink: "{n} active applications",
    navNachrichtenTitle: "Messages", navNachrichtenDesc: "Send requests to IGAplus and manage tickets.", navNachrichtenLink: "{n} unread messages",
    navProfilTitle: "Profile", navProfilDesc: "Manage clinic details and settings.", navProfilLink: "Manage settings",
    actHired: "Contract signed", actNew: "New application", actMovedTo: "Moved to “{x}”", actDoc: "{doc} uploaded",
    rtNow: "just now", rtMin: "{n}m ago", rtHour: "{n}h ago", rtYesterday: "Yesterday", rtDays: "{n}d ago",
  },
  ru: {
    welcome: "С возвращением", navHeading: "Куда вы хотите перейти?", activityHeading: "Последние действия", activityShowAll: "Показать все",
    statBewerber: "Кандидаты", statBewerbungen: "Заявки", statEingestellt: "Приняты",
    navKandidatenTitle: "Кандидаты", navKandidatenDesc: "Поиск медсестёр и просмотр профилей.", navKandidatenLink: "Доступно {n} кандидатов",
    navBewerbungenTitle: "Заявки", navBewerbungenDesc: "Управляйте активными заявками и этапами.", navBewerbungenLink: "{n} активных заявок",
    navNachrichtenTitle: "Сообщения", navNachrichtenDesc: "Отправляйте запросы в IGAplus и управляйте тикетами.", navNachrichtenLink: "{n} непрочитанных сообщений",
    navProfilTitle: "Профиль", navProfilDesc: "Управление данными клиники и настройками.", navProfilLink: "Управление настройками",
    actHired: "Договор подписан", actNew: "Новая заявка", actMovedTo: "Перемещено в «{x}»", actDoc: "{doc} загружен",
    rtNow: "только что", rtMin: "{n} мин назад", rtHour: "{n} ч назад", rtYesterday: "Вчера", rtDays: "{n} дн назад",
  },
  tr: {
    welcome: "Tekrar hoş geldiniz", navHeading: "Nereye gitmek istersiniz?", activityHeading: "Son etkinlikler", activityShowAll: "Tümünü gör",
    statBewerber: "Adaylar", statBewerbungen: "Başvurular", statEingestellt: "İşe alınan",
    navKandidatenTitle: "Adaylar", navKandidatenDesc: "Hemşireleri arayın ve profilleri görüntüleyin.", navKandidatenLink: "{n} aday mevcut",
    navBewerbungenTitle: "Başvurular", navBewerbungenDesc: "Aktif başvuruları yönetin ve süreci izleyin.", navBewerbungenLink: "{n} aktif başvuru",
    navNachrichtenTitle: "Mesajlar", navNachrichtenDesc: "IGAplus'a talep gönderin ve talepleri yönetin.", navNachrichtenLink: "{n} okunmamış mesaj",
    navProfilTitle: "Profil", navProfilDesc: "Klinik bilgilerini ve ayarları yönetin.", navProfilLink: "Ayarları yönet",
    actHired: "Sözleşme imzalandı", actNew: "Yeni başvuru", actMovedTo: "„{x}\" aşamasına taşındı", actDoc: "{doc} yüklendi",
    rtNow: "şimdi", rtMin: "{n} dk önce", rtHour: "{n} sa önce", rtYesterday: "Dün", rtDays: "{n} gün önce",
  },
};

export default {
  name: "ClinicDashboardPage",
  data() {
    return {
      profile: { companyName: "", email: "" },
      counts: { bewerber: 0, bewerbungen: 0, eingestellt: 0 },
      unread: 0,
      activities: [],
      icons: ICONS,
      loading: true,
    };
  },
  computed: {
    lang() { return this.$store.state.lang || "az"; },
    displayName() {
      return this.profile.companyName || this.profile.email || "";
    },
    subtitle() {
      // Hələlik — sonra dəqiqləşəcək
      return this.profile.email || "";
    },
    greeting() { return this.t("welcome"); },
    initials() {
      const src = (this.profile.companyName || this.profile.email || "").trim();
      const parts = src.split(/\s+/).filter((p) => /^[\p{L}]/u.test(p));
      const f = (parts[0] || "").charAt(0).toUpperCase();
      const s = (parts[1] || "").charAt(0).toUpperCase();
      return (f + s) || (src.slice(0, 2).toUpperCase() || "?");
    },
    today() {
      const loc = LOCALES[this.lang] || "az-AZ";
      try {
        return new Date().toLocaleDateString(loc, {
          weekday: "long", day: "numeric", month: "long", year: "numeric",
        });
      } catch {
        return "";
      }
    },
    stats() {
      return [
        { key: "bewerber", txKey: "ClinicDashboardStatBewerber", label: this.t("statBewerber"), dot: "#294C8E", count: this.counts.bewerber },
        { key: "bewerbungen", txKey: "ClinicDashboardStatBewerbungen", label: this.t("statBewerbungen"), dot: "#894FE5", count: this.counts.bewerbungen },
        { key: "eingestellt", txKey: "ClinicDashboardStatEingestellt", label: this.t("statEingestellt"), dot: "#10B881", count: this.counts.eingestellt },
      ];
    },
    navHeading() { return this.t("navHeading"); },
    activityHeading() { return this.t("activityHeading"); },
    activityShowAll() { return this.t("activityShowAll"); },
    navCards() {
      return [
        {
          key: "kandidaten", accent: "#132B59", icon: "user", to: "candidates",
          titleKey: "ClinicDashboardNavKandidatenTitle", descKey: "ClinicDashboardNavKandidatenDesc", linkKey: "ClinicDashboardNavKandidatenLink",
          title: this.t("navKandidatenTitle"),
          desc: this.t("navKandidatenDesc"),
          link: this.t("navKandidatenLink").replace("{n}", this.counts.bewerber),
        },
        {
          key: "bewerbungen", accent: "#894FE5", icon: "clipboard", to: "my-applications",
          titleKey: "ClinicDashboardNavBewerbungenTitle", descKey: "ClinicDashboardNavBewerbungenDesc", linkKey: "ClinicDashboardNavBewerbungenLink",
          title: this.t("navBewerbungenTitle"),
          desc: this.t("navBewerbungenDesc"),
          link: this.t("navBewerbungenLink").replace("{n}", this.counts.bewerbungen),
        },
        {
          key: "nachrichten", accent: "#0FA5C6", icon: "mail", to: "clinic-nachrichten",
          titleKey: "ClinicDashboardNavNachrichtenTitle", descKey: "ClinicDashboardNavNachrichtenDesc", linkKey: "ClinicDashboardNavNachrichtenLink",
          title: this.t("navNachrichtenTitle"),
          desc: this.t("navNachrichtenDesc"),
          link: this.t("navNachrichtenLink").replace("{n}", this.unread),
          badge: this.unread,
        },
        {
          key: "profil", accent: "#132B59", icon: "cog", to: "clinic-settings",
          titleKey: "ClinicDashboardNavProfilTitle", descKey: "ClinicDashboardNavProfilDesc", linkKey: "ClinicDashboardNavProfilLink",
          title: this.t("navProfilTitle"),
          desc: this.t("navProfilDesc"),
          link: this.t("navProfilLink"),
        },
      ];
    },
  },
  mounted() { this.load(); },
  methods: {
    t(key) {
      // Admin paneldən idarə olunan override — açar: ClinicDashboard + Key
      const sKey = "ClinicDashboard" + key.charAt(0).toUpperCase() + key.slice(1);
      const ov = this.$store.state.statics && this.$store.state.statics[sKey];
      if (ov && String(ov).trim()) return ov;
      const d = T[this.lang] || T.az;
      return d[key] != null ? d[key] : (T.az[key] || key);
    },
    statusLabel(code) { const m = STATUS_T[code]; return m ? (m[this.lang] || m.az) : (code || ""); },
    async load() {
      this.loading = true;
      try {
        const res = await axios.get(`/clinic/profile`);
        const p = res.data?.data || {};
        this.profile = { companyName: p.companyName || "", email: p.email || "" };
      } finally {
        this.loading = false;
      }
      this.loadCounts();
      this.loadActivities();
    },
    async loadCounts() {
      // Bewerbungen + Eingestellt — klinikanın müraciətlərindən
      try {
        const res = await axios.get(`/clinic/applications`);
        const data = res.data?.data;
        const items = Array.isArray(data) ? data : (data?.items || []);
        const sc = (data && data.statusCounts) || {};
        this.counts.bewerbungen = items.length;
        this.counts.eingestellt = sc.Eingestellt || 0;
      } catch { /* ignore */ }

      // Bewerber — mövcud namizədlərin ümumi sayı
      try {
        const res = await axios.get(`/clinic/nurses`, { params: { PageNumber: 1, PageSize: 1 } });
        this.counts.bewerber = res.data?.data?.paging?.totalItems || 0;
      } catch { /* ignore */ }
    },
    goCard(c) {
      if (!c.to) {
        // Nachrichten — mesajlaşma hələ qurulmayıb
        this.$toast.info("Tezliklə");
        return;
      }
      this.$router.push({ name: c.to });
    },
    async loadActivities() {
      try {
        const res = await axios.get(`/clinic/activities`, { params: { top: 3 } });
        this.activities = res.data?.data || [];
      } catch { this.activities = []; }
    },
    avatarFor(id) {
      const idx = (Number(id) || 0) % ACTIVITY_PALETTE.length;
      return ACTIVITY_PALETTE[idx];
    },
    nameInitials(name) {
      const parts = (name || "").trim().split(/\s+/).filter((p) => /^[\p{L}]/u.test(p));
      const f = (parts[0] || "").charAt(0).toUpperCase();
      const s = (parts[1] || "").charAt(0).toUpperCase();
      return (f + s) || "?";
    },
    activityText(a) {
      if (a.type === "document") {
        return this.t("actDoc").replace("{doc}", a.documentName || "Dokument");
      }
      if (a.statusCode === "Eingestellt") return this.t("actHired");
      if (a.statusCode === "Neu") return this.t("actNew");
      return this.t("actMovedTo").replace("{x}", this.statusLabel(a.statusCode));
    },
    relativeTime(d) {
      if (!d) return "";
      const date = new Date(d);
      const min = Math.floor((Date.now() - date.getTime()) / 60000);
      if (min < 1) return this.t("rtNow");
      if (min < 60) return this.t("rtMin").replace("{n}", min);
      const h = Math.floor(min / 60);
      if (h < 24) return this.t("rtHour").replace("{n}", h);
      const days = Math.floor(h / 24);
      if (days === 1) return this.t("rtYesterday");
      if (days < 7) return this.t("rtDays").replace("{n}", days);
      try {
        return date.toLocaleDateString(LOCALES[this.lang] || "az-AZ", { day: "2-digit", month: "2-digit", year: "numeric" });
      } catch { return ""; }
    },
  },
};
</script>

<style lang="scss" scoped>
.clinic-dashboard {
  min-height: 60vh;
  max-width: 1120px;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
}

.welcome-card {
  width: 100%; max-width: 1080px; min-height: 88px;
  border: 1px solid #E0E3EA; border-radius: 14px; background: #fff;
  display: flex; align-items: center; gap: 16px;
  padding: 18px 24px;

  &__avatar {
    width: 52px; height: 52px; flex-shrink: 0; border-radius: 50%;
    background: #132B59; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;
  }

  &__main { flex: 1; min-width: 0; }

  &__title {
    margin: 0; font-size: 20px; font-weight: 700; color: #151924; line-height: 1;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  &__sub {
    margin: 9px 0 0; font-size: 13px; font-weight: 400; color: #666F82; line-height: 1;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  &__date {
    flex-shrink: 0; font-size: 12px; font-weight: 400; color: #9EA6B6; line-height: 1;
    align-self: center; white-space: nowrap;
  }

  @media (max-width: 575px) {
    flex-wrap: wrap;
    &__date { width: 100%; margin-top: 8px; }
  }
}

.stats-card {
  width: 100%; max-width: 1080px; min-height: 56px;
  border: 1px solid #E0E3EA; border-radius: 12px; background: #fff;
  display: flex; align-items: stretch; margin-top: 16px;

  .stat {
    flex: 1; min-width: 0; position: relative;
    display: flex; flex-direction: column; justify-content: center; gap: 7px;
    padding: 12px 20px;

    // 36px hündürlüklü ayırıcı (mərkəzdə)
    &:not(:first-child)::before {
      content: ""; position: absolute; left: 0; top: 50%;
      transform: translateY(-50%);
      width: 1px; height: 36px; background: #EBEDF1;
    }

    .stat-top { display: flex; align-items: center; gap: 8px;
      .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
      .num { font-size: 18px; font-weight: 700; line-height: 1; color: #151924; }
    }
    .label { font-size: 11px; font-weight: 500; color: #666F82; line-height: 1; }
  }

  @media (max-width: 575px) {
    flex-wrap: wrap;
    .stat { flex: 0 0 100%;
      &:not(:first-child)::before { display: none; }
      &:not(:first-child) { border-top: 1px solid #EBEDF1; }
    }
  }
}

.nav-heading {
  max-width: 1080px; margin: 28px 0 14px;
  font-size: 13px; font-weight: 400; color: #666F82; line-height: 1;
}

.nav-grid {
  width: 100%; max-width: 1080px;
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;

  @media (max-width: 991px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 575px) { grid-template-columns: 1fr; }
}

.nav-card {
  position: relative; min-height: 216px;
  border: 1px solid #E0E3EA; border-radius: 16px; background: #fff;
  padding: 24px 20px 20px;
  display: flex; flex-direction: column;
  cursor: pointer; overflow: hidden;
  transition: box-shadow 0.12s ease, transform 0.12s ease, border-color 0.12s ease;

  // üst rəngli zolaq
  &::before {
    content: ""; position: absolute; top: 0; left: 0; right: 0; height: 4px;
    background: var(--accent);
  }

  &:hover { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(16,24,40,0.10); border-color: #d8e2f3; }

  &__icon {
    position: relative;
    width: 48px; height: 48px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 16px;
    svg { width: 24px; height: 24px; }
  }

  &__badge {
    position: absolute; top: -4px; right: -4px;
    min-width: 18px; height: 18px; padding: 0 5px;
    background: #EF4444; color: #fff; border-radius: 9px;
    font-size: 10px; font-weight: 700; line-height: 18px; text-align: center;
  }

  &__title { margin: 0 0 8px; font-size: 17px; font-weight: 700; color: #151924; line-height: 1; }

  &__desc { margin: 0; font-size: 11px; font-weight: 400; color: #666F82; line-height: 1.5; }

  &__link {
    margin-top: auto; display: flex; align-items: center; justify-content: space-between;
    font-size: 11px; font-weight: 500; line-height: 1;
    .arrow { font-size: 13px; }
  }
}

.activity-head {
  max-width: 1080px; margin: 28px 0 12px;
  display: flex; align-items: center; justify-content: space-between;
  &__title { font-size: 13px; font-weight: 400; color: #666F82; line-height: 1; }
  &__link { font-size: 13px; font-weight: 500; color: #132B59; text-decoration: none; line-height: 1;
    .arrow { font-size: 14px; }
    &:hover { text-decoration: underline; }
  }
}

.activity-list {
  width: 100%; max-width: 1080px;
  border: 1px solid #E0E3EA; border-radius: 12px; background: #fff; overflow: hidden;
  display: flex; align-items: stretch;

  .activity {
    flex: 1; min-width: 0;
    display: flex; align-items: center; gap: 12px;
    padding: 16px 20px;
    &:not(:first-child) { border-left: 1px solid #EBEDF1; }

    &__avatar {
      width: 36px; height: 36px; flex-shrink: 0; border-radius: 50%;
      color: #fff; font-size: 12px; font-weight: 700; text-transform: uppercase;
      display: flex; align-items: center; justify-content: center;
    }
    &__main { flex: 1; min-width: 0; }
    &__name { font-size: 13px; font-weight: 700; color: #151924; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    &__desc { font-size: 12px; font-weight: 400; color: #666F82; line-height: 1.3; margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    &__time { flex-shrink: 0; font-size: 12px; color: #9EA6B6; white-space: nowrap; }
  }

  @media (max-width: 767px) {
    flex-direction: column; align-items: stretch;
    .activity {
      &:not(:first-child) { border-left: 0; border-top: 1px solid #EBEDF1; }
    }
  }
}

.activity-empty { max-width: 1080px; padding: 16px; text-align: center; color: #9EA6B6; border: 1px solid #E0E3EA; border-radius: 12px; background: #fff; }
</style>
